import React, { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import bb from 'billboard.js/dist/billboard.pkgd';
import 'billboard.js/dist/billboard.css';
import 'billboard.js/dist/theme/insight.css';
import './AbstractStatusChart.scss';
// Icons
import { Grid } from '@mui/material';
import {
  classesAddedToDocument,
  createCustomCssClassForDataRegionFill,
  getRegionClassNameByRegionColor,
} from '../../styles';
import { UI_DATE_FORMAT } from '../../constants';
import LelySpinner from '../muiWrappers/LelySpinner/LelySpinner';
/** Graph - Status chart.
 * @param  requestName - Name of the chart request
 * @param  idOfElement - Name of the element
 * @param requestNames - Array of pieces of data to be requested to the backend
 * @param explicitChartData - Data to be used instead of using data from the Store or the backend
 */
export default function AbstractStatusChart({
  requestName,
  idOfElement,
  explicitChartData,
  startDate,
  endDate,
}) {
  /** Entity Combine Chart Data */

  const graphDetails = {
    Milking: explicitChartData.systemState,
  };
  const chartRef = useRef(null);

  /** Update DOM element with new chart */
  useEffect(() => {
    if (graphDetails[requestName] || explicitChartData) {
      renderChart();
    }
  }, [graphDetails[requestName], explicitChartData]);

  let graphRegions = [];
  const scatterData = {};
  let graphTitle = '';
  const graphHexColors = [];
  const graphValues = [];
  const graphDates = [];

  /** Create array with all dates (horizontal) */
  const chartData =
    (graphDetails && graphDetails[requestName]) || explicitChartData;
  const regionColorToStatusDictionary = {};

  if (chartData) {
    graphTitle = chartData.name;

    let latestOperationStatus = '';

    chartData?.axesData?.forEach((item) => {
      const currentOperationStatus = item.y;
      if (currentOperationStatus !== latestOperationStatus) {
        graphValues.push(item.y);
        graphDates.push(new Date(item.x));
      }
      latestOperationStatus = currentOperationStatus;
    });

    const dataKeyToColorDictionary = {};

    chartData?.colors?.forEach((item) => {
      dataKeyToColorDictionary[item.value] = item.hex;
    });

    let lastScatterDataIndex = 0;
    const scatterIndices = {};
    graphValues.forEach((item) => {
      if (!scatterData[item]) {
        scatterData[item] = [item];
        scatterIndices[item] = lastScatterDataIndex;
        lastScatterDataIndex++;
      }
    });
    graphValues.forEach((item, index) => {
      for (const key in scatterData) {
        if (key !== item) {
          scatterData[key].push(null);
        } else {
          scatterData[key].push(new Date(graphDates[index]));
        }
      }
    });

    graphRegions = [];
    graphValues.forEach((item, index) => {
      const nextDate =
        index < graphDates.length - 1
          ? new Date(graphDates[index + 1])
          : new Date(endDate);
      const regionClassName = getRegionClassNameByRegionColor(
        dataKeyToColorDictionary[item],
      );
      graphRegions.push({
        axis: 'x',
        start: graphDates[index] ? new Date(graphDates[index]) : null,
        end: nextDate,
        class: regionClassName,
      });
      // eslint-disable-next-line no-restricted-globals
      const isDateNaN = !isNaN(graphDates[index]);
      regionColorToStatusDictionary[`bb-region-${index}`] = `Date: ${(
        isDateNaN && format(new Date(graphDates[index]), UI_DATE_FORMAT)
      ).toString()}\nStatus: ${item}`;
    });

    chartData?.colors?.forEach((value) => {
      if (classesAddedToDocument.indexOf(value.hex) === -1) {
        createCustomCssClassForDataRegionFill(value.hex, 'test');
        classesAddedToDocument.push(value.hex);
      }
      graphHexColors.push(value.hex);
    });
  }

  let renderChart = () => {
    if (graphDates) {
      const columnsObject = [];
      const typesObject = {};
      const tickNames = [];
      columnsObject.push(['x', ...graphDates.concat(new Date(endDate))]);
      for (const key in scatterData) {
        tickNames.push(key);
        typesObject[key] = 'scatter';
        const currentColumn = scatterData[key];
        columnsObject.push(currentColumn);
      }
      const firstColumnName = columnsObject[1] && columnsObject[1][0];
      const graphNamesObject = { names: {} };
      graphNamesObject[firstColumnName] = graphTitle.toUpperCase();
      const chart = bb.generate({
        bindto: `#${idOfElement}`,
        size: {
          height: 100,
        },
        onrendered() {
          setTimeout(() => {
            if (
              chartRef &&
              chartRef.current &&
              chartRef.current.getElementsByClassName('bb-region')
            ) {
              const regions =
                chartRef.current.getElementsByClassName('bb-region');
              for (let i = 0; i < regions.length; i++) {
                const currentRegion = regions.item(i);
                const regionClass = currentRegion.classList.item(1);
                const rect = currentRegion.getElementsByTagName('rect')[0];
                const titleElement = document.createElementNS(
                  'http://www.w3.org/2000/svg',
                  'title',
                );
                titleElement.textContent =
                  regionColorToStatusDictionary[regionClass];
                rect.appendChild(titleElement);
              }
            }
          }, 200);
        },
        data: {
          x: 'x',
          columns: columnsObject,
          colors: columnsObject.map((currentColumnName, currentColumnIndex) => {
            const colorObject = {};
            colorObject[currentColumnName] = graphHexColors[currentColumnIndex];
            return colorObject;
          }),
          type: 'scatter',
          types: typesObject,
        },
        legend: {
          show: false,
          contents: {
            bindto: `#legend-${idOfElement}`,
            template(title, color) {
              return `<div style="padding: 0 10px;"><span class="legend_tile" style="background:${color}"></span>
              <span class="legend_text fs--b-sm" >${title}</span></div>`;
            },
          },
        },
        axis: {
          x: {
            tick: {
              values: [new Date(startDate), new Date(endDate)],
              count: 5,
              format: '%Y-%m-%d %H:%M:%S',
            },
            type: 'timeseries',
          },
          y: {
            center: 200,
            show: false,
            label: {
              text: '',
            },
            tick: {
              values: tickNames,
              format(x) {
                return x;
              },
            },
          },
        },
        point: {
          r: 5,
        },
        clipPath: false,
        interaction: {
          enabled: true,
        },
        color: {
          pattern: graphHexColors,
        },
        title: {
          text: requestName && requestName.toUpperCase(),
          padding: {
            bottom: 5,
          },
        },
        resize: {
          auto: true,
        },
        zoom: {
          enabled: false,
        },
        padding: {
          right: 15,
          left: 40,
          bottom: 20,
        },
      });
      chart.regions(graphRegions);
    }
  };

  if (chartData) {
    return (
      <div className="wrapper abstract-status-chart-wrapper" ref={chartRef}>
        <div className="btnWrapper" />
        <Grid container>
          <Grid size={10} item>
            <div id={idOfElement} />
          </Grid>
          <Grid size={2} item className="legend_container">
            <div id={`legend-${idOfElement}`} />
          </Grid>
        </Grid>
      </div>
    );
  }
  return (
    <div className="spinner-style">
      <LelySpinner />
    </div>
  );
}
